import { Box, CircularProgress, Link, Paper, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink, useParams } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import { useMe } from '@/api/session'
import type { Invite as InviteData, RsvpStatus } from '@/api/types'
import { AskFields } from '@/components/AskFields'
import { Footer } from '@/components/Footer'
import { InviteCard } from '@/components/InviteCard'
import { PublicHeader } from '@/components/PublicHeader'

/**
 * The invite, for somebody holding a per-person link.
 *
 * **It asks for no account, and that is the point.** Partiful, Evite and
 * Paperless Post do not make a guest sign in to say yes; the token in the URL
 * is the credential, exactly as it is on the pay page.
 *
 * A page of its own rather than a mode of `Pay.tsx`, which is money all the
 * way down — amount, handle, rail, reference code — and shares its frame with
 * this and none of its content. Making one component render two products is
 * how the pay page and the join page drifted into disagreeing about what a
 * stranger may see.
 */
export function Invite() {
  const { token = '' } = useParams()
  const queryClient = useQueryClient()

  const page = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<InviteData>(`/api/r/${token}`),
  })

  /**
   * Both mutations return the whole page, which is what stops this screen
   * computing its own next state. The server decides what an answer means.
   */
  const respond = useMutation({
    mutationFn: (body: { status: RsvpStatus; guest_count: number }) =>
      api.post<InviteData>(`/api/r/${token}`, body),
    onSuccess: (data) => queryClient.setQueryData(['invite', token], data),
  })

  const withdraw = useMutation({
    mutationFn: () => api.del<InviteData>(`/api/r/${token}`),
    onSuccess: (data) => queryClient.setQueryData(['invite', token], data),
  })

  // Its own route, so a song added a week later does not restate whether
  // somebody is coming — and so a half-filled form cannot carry the answer
  // that is actually being chased.
  const answer = useMutation({
    mutationFn: (answers: Record<string, string>) =>
      api.put<InviteData>(`/api/r/${token}/answers`, { answers }),
    onSuccess: (data) => queryClient.setQueryData(['invite', token], data),
  })

  if (page.isPending) {
    return (
      <Centered>
        <CircularProgress size={22} />
      </Centered>
    )
  }

  if (page.error) {
    return (
      <Centered>
        <Paper sx={{ p: 3, maxWidth: 380 }}>
          <PublicHeader />
          <Typography variant="h2" sx={{ mb: 1 }}>
            This link does not work
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {(page.error as ApiError).detail ||
              'It may have been withdrawn. Ask whoever sent it for a fresh one.'}
          </Typography>
        </Paper>
      </Centered>
    )
  }

  return (
    <Centered>
      <Paper sx={{ p: 3, width: '100%', maxWidth: 400 }}>
        <PublicHeader />
        <Box sx={{ mt: 1.5 }}>
          <InviteCard
            invite={page.data}
            busy={respond.isPending || withdraw.isPending}
            onRespond={(status, guest_count) =>
              respond.mutate({ status, guest_count })
            }
            onWithdraw={() => withdraw.mutate()}
          />
          {/* Only once they are coming. Somebody who said no has nothing to
              tell us about the food, and being asked anyway reads as a
              product that was not listening. */}
          {page.data.rsvp_status === 'going' && (
            <AskFields
              asks={page.data.asks ?? []}
              answers={page.data.answers ?? {}}
              suggestions={page.data.suggestions ?? {}}
              allergens={page.data.allergens ?? []}
              busy={answer.isPending}
              disabled={page.data.closed}
              onSave={(answers) => answer.mutate(answers)}
            />
          )}
        </Box>
      </Paper>
    </Centered>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  // The same frame as the pay page, and for the same reason: a way back for
  // somebody who arrived from their own dashboard, nothing for a stranger.
  const me = useMe()
  return (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        alignContent: 'center',
        minHeight: '100dvh',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      {me.data?.signed_in && (
        <Box sx={{ width: '100%', maxWidth: 400, mb: 1.5 }}>
          <Link
            component={RouterLink}
            to="/"
            variant="caption"
            underline="hover"
            sx={{ color: 'text.secondary' }}
          >
            ← Your campaigns
          </Link>
        </Box>
      )}
      {children}
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Footer />
      </Box>
    </Box>
  )
}
